import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Search, Trash2 } from "lucide-react";

const empty = { name: "", phone: "", id_number: "", address: "", notes: "" };

export default function Customers() {
  const { t, user } = useApp();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    setList(data);
  };

  useEffect(() => {
    const tm = setTimeout(load, 200);
    return () => clearTimeout(tm);
    // eslint-disable-next-line
  }, [q]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/customers", form);
      toast.success(t("customer_saved"));
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!confirm("Delete customer?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t("customers")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
              <UserPlus className="w-4 h-4 mr-2" /> {t("add_customer")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader><DialogTitle>{t("add_customer")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {["name", "phone", "id_number", "address"].map((k) => (
                <div key={k}>
                  <Label>{t(k)}</Label>
                  <Input data-testid={`cust-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" />
                </div>
              ))}
              <div>
                <Label>{t("notes")}</Label>
                <Textarea data-testid="cust-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="cust-save" onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">{t("save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 glass border-slate-800">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" />
        </div>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && <div className="text-slate-500 col-span-full text-center py-12">No customers yet</div>}
        {list.map((c) => (
          <Card key={c.id} data-testid={`cust-card-${c.id}`} className="p-4 glass border-slate-800 hover-lift">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-xs text-slate-400 font-mono">{c.phone || "—"}</div>
                {c.id_number && <div className="text-xs text-slate-500 mt-1">ID: {c.id_number}</div>}
                {c.address && <div className="text-xs text-slate-500 truncate mt-1">{c.address}</div>}
              </div>
              {user?.role === "admin" && (
                <button data-testid={`cust-del-${c.id}`} onClick={() => del(c.id)} className="text-slate-500 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {c.notes && <div className="mt-3 text-xs text-slate-400 border-t border-slate-800 pt-2">{c.notes}</div>}
          </Card>
        ))}
      </div>
    </div>
  );
}
